import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Container,
  Alert,
  Card,
  CardContent,
  CardActionArea,
  Grid
} from '@mui/material';
import { Upload, ArrowLeft, ArrowRight, Check, Users, UserCircle, Building, DollarSign, GraduationCap } from 'lucide-react';
import { parseCSV, ParsedCSV } from '../../utils/csvParser';
import { UploadStep } from './BulkImport/UploadStep';
import { MappingStep } from './BulkImport/MappingStep';
import { PreviewStep } from './BulkImport/PreviewStep';
import { ConfirmationStep } from './BulkImport/ConfirmationStep';
import { useData } from '../../contexts/DataContext';
import { v4 as uuidv4 } from 'uuid';

const steps = ['Upload CSV', 'Map Fields', 'Preview Data', 'Confirm Import'];

export type ImportType = 'students' | 'parents' | 'community' | 'fees' | 'courses';

export interface FieldMapping {
  systemField: string;
  csvColumn: string | null;
  csvColumnIndex: number | null;
}

export interface RelationshipConfig {
  id: string;
  targetEntity: 'students' | 'parents' | 'courses' | 'buildings';
  targetMatchField: string;
  label: string;
  description: string;
}

export interface RelationshipMapping {
  relationshipId: string;
  csvColumn: string | null;
  csvColumnIndex: number | null;
}

export interface RelationshipMatch {
  csvValue: string;
  matchedId: string | null;
  matchedLabel: string | null;
}

export interface ImportedRecord {
  id: string;
  data: Record<string, string>;
  isValid: boolean;
  errors: string[];
  relationshipMatches?: Record<string, RelationshipMatch>;
}

export const RELATIONSHIP_CONFIGS: Record<ImportType, RelationshipConfig[]> = {
  students: [
    {
      id: 'parent_email',
      targetEntity: 'parents',
      targetMatchField: 'email',
      label: 'Parent Email',
      description: 'Link students to parents by parent email address'
    },
    {
      id: 'course_name',
      targetEntity: 'courses',
      targetMatchField: 'name',
      label: 'Course Name',
      description: 'Enroll students in a course by course name'
    }
  ],
  parents: [
    {
      id: 'student_id',
      targetEntity: 'students',
      targetMatchField: 'studentId',
      label: 'Student ID',
      description: 'Link parents to students by student ID'
    },
    {
      id: 'student_email',
      targetEntity: 'students',
      targetMatchField: 'email',
      label: 'Student Email',
      description: 'Link parents to students by student email address'
    }
  ],
  community: [
    {
      id: 'student_id',
      targetEntity: 'students',
      targetMatchField: 'studentId',
      label: 'Student ID',
      description: 'Link community members to students by student ID'
    }
  ],
  fees: [
    {
      id: 'course_name',
      targetEntity: 'courses',
      targetMatchField: 'name',
      label: 'Course Name',
      description: 'Associate fees with a course by course name'
    }
  ],
  courses: [
    {
      id: 'building_name',
      targetEntity: 'buildings',
      targetMatchField: 'name',
      label: 'Building Name',
      description: 'Assign courses to a building by building name'
    }
  ]
};

export const BulkImport = () => {
  const { students, parents, courses, buildings } = useData();
  const [activeStep, setActiveStep] = useState(0);
  const [importType, setImportType] = useState<ImportType | null>(null);
  const [parsedCSV, setParsedCSV] = useState<ParsedCSV | null>(null);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [relationshipMappings, setRelationshipMappings] = useState<RelationshipMapping[]>([]);
  const [importedRecords, setImportedRecords] = useState<ImportedRecord[]>([]);
  const [error, setError] = useState<string>('');
  const [importComplete, setImportComplete] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [relationshipStats, setRelationshipStats] = useState<Record<string, { matched: number; unmatched: number }>>({});

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = parseCSV(content);
        setParsedCSV(parsed);
        initializeFieldMappings(parsed);
        setError('');
        setActiveStep(1);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to parse CSV');
      }
    };
    reader.readAsText(file);
  };

  const initializeFieldMappings = (csv: ParsedCSV) => {
    if (!importType) return;
    const systemFields = getSystemFields(importType);
    const mappings: FieldMapping[] = systemFields.map(field => {
      const matchingColumn = csv.columns.find(col =>
        col.name.toLowerCase().replace(/[^a-z0-9]/g, '') ===
        field.toLowerCase().replace(/[^a-z0-9]/g, '')
      );

      return {
        systemField: field,
        csvColumn: matchingColumn?.name || null,
        csvColumnIndex: matchingColumn?.index ?? null
      };
    });

    setFieldMappings(mappings);

    const relationshipConfigs = RELATIONSHIP_CONFIGS[importType] || [];
    const relMappings: RelationshipMapping[] = relationshipConfigs.map(config => ({
      relationshipId: config.id,
      csvColumn: null,
      csvColumnIndex: null
    }));
    setRelationshipMappings(relMappings);
  };

  const resolveRelationship = (
    value: string,
    config: RelationshipConfig
  ): RelationshipMatch => {
    if (!value || value.trim() === '') {
      return { csvValue: value, matchedId: null, matchedLabel: null };
    }

    const normalizedValue = value.toLowerCase().trim();
    let matchedRecord: { id: string; label: string } | null = null;

    switch (config.targetEntity) {
      case 'students': {
        const student = students.find(s => {
          if (config.targetMatchField === 'studentId') {
            return s.studentId.toLowerCase() === normalizedValue;
          }
          if (config.targetMatchField === 'email') {
            return s.email.toLowerCase() === normalizedValue;
          }
          return false;
        });
        if (student) {
          matchedRecord = { id: student.id, label: `${student.firstName} ${student.lastName} (${student.studentId})` };
        }
        break;
      }
      case 'parents': {
        const parent = parents.find(p => {
          if (config.targetMatchField === 'email') {
            return p.email.toLowerCase() === normalizedValue;
          }
          return false;
        });
        if (parent) {
          matchedRecord = { id: parent.id, label: `${parent.firstName} ${parent.lastName}` };
        }
        break;
      }
      case 'courses': {
        const course = courses.find(c => {
          if (config.targetMatchField === 'name') {
            return c.name.toLowerCase() === normalizedValue;
          }
          return false;
        });
        if (course) {
          matchedRecord = { id: course.id, label: course.name };
        }
        break;
      }
      case 'buildings': {
        const building = buildings.find(b => {
          if (config.targetMatchField === 'name') {
            return b.name.toLowerCase() === normalizedValue;
          }
          return false;
        });
        if (building) {
          matchedRecord = { id: building.id, label: building.name };
        }
        break;
      }
    }

    return {
      csvValue: value,
      matchedId: matchedRecord?.id || null,
      matchedLabel: matchedRecord?.label || null
    };
  };

  const getSystemFields = (type: ImportType): string[] => {
    switch (type) {
      case 'students':
        return ['firstName', 'lastName', 'studentId', 'email', 'phone', 'gradeLevel', 'dateOfBirth', 'address', 'city', 'state', 'zipCode'];
      case 'parents':
        return ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'zipCode'];
      case 'community':
        return ['firstName', 'lastName', 'email', 'phone', 'organization', 'role'];
      case 'fees':
        return ['name', 'description', 'amount', 'category', 'dueDate', 'isRequired', 'allowMultiple', 'maxQuantity'];
      case 'courses':
        return ['name', 'description', 'instructor', 'capacity', 'schedule', 'location', 'startDate', 'endDate'];
      default:
        return [];
    }
  };

  const handleMappingComplete = () => {
    if (!parsedCSV || !importType) return;

    const relationshipConfigs = RELATIONSHIP_CONFIGS[importType] || [];
    const stats: Record<string, { matched: number; unmatched: number }> = {};

    relationshipMappings.forEach(mapping => {
      stats[mapping.relationshipId] = { matched: 0, unmatched: 0 };
    });

    const records: ImportedRecord[] = parsedCSV.rows.map(row => {
      const data: Record<string, string> = {};
      const errors: string[] = [];

      fieldMappings.forEach(mapping => {
        if (mapping.csvColumnIndex !== null) {
          data[mapping.systemField] = row[mapping.csvColumnIndex] || '';
        }
      });

      const relationshipMatches: Record<string, RelationshipMatch> = {};

      relationshipMappings.forEach(relMapping => {
        if (relMapping.csvColumnIndex !== null) {
          const config = relationshipConfigs.find(c => c.id === relMapping.relationshipId);
          if (config) {
            const csvValue = row[relMapping.csvColumnIndex] || '';
            const match = resolveRelationship(csvValue, config);
            relationshipMatches[relMapping.relationshipId] = match;

            if (csvValue && csvValue.trim() !== '') {
              if (match.matchedId) {
                stats[relMapping.relationshipId].matched++;
              } else {
                stats[relMapping.relationshipId].unmatched++;
              }
            }
          }
        }
      });

      const validation = validateRecord(data, importType);

      return {
        id: uuidv4(),
        data,
        isValid: validation.isValid,
        errors: validation.errors,
        relationshipMatches
      };
    });

    setRelationshipStats(stats);
    setImportedRecords(records);
    setActiveStep(2);
  };

  const validateRecord = (data: Record<string, string>, type: ImportType): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (type === 'students') {
      if (!data.firstName) errors.push('First name is required');
      if (!data.lastName) errors.push('Last name is required');
      if (!data.studentId) errors.push('Student ID is required');
      if (!data.email) errors.push('Email is required');
      if (data.email && !isValidEmail(data.email)) errors.push('Invalid email format');
    } else if (type === 'parents') {
      if (!data.firstName) errors.push('First name is required');
      if (!data.lastName) errors.push('Last name is required');
      if (!data.email) errors.push('Email is required');
      if (data.email && !isValidEmail(data.email)) errors.push('Invalid email format');
    } else if (type === 'community') {
      if (!data.firstName) errors.push('First name is required');
      if (!data.lastName) errors.push('Last name is required');
      if (!data.email) errors.push('Email is required');
      if (data.email && !isValidEmail(data.email)) errors.push('Invalid email format');
    } else if (type === 'fees') {
      if (!data.name) errors.push('Name is required');
      if (!data.amount) errors.push('Amount is required');
      if (data.amount && isNaN(parseFloat(data.amount))) errors.push('Amount must be a valid number');
      if (!data.category) errors.push('Category is required');
    } else if (type === 'courses') {
      if (!data.name) errors.push('Name is required');
      if (!data.instructor) errors.push('Instructor is required');
      if (data.capacity && isNaN(parseInt(data.capacity))) errors.push('Capacity must be a valid number');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleConfirmImport = () => {
    const validRecords = importedRecords.filter(r => r.isValid);
    setImportedCount(validRecords.length);
    setImportComplete(true);
    setActiveStep(3);
  };

  const handleReset = () => {
    setActiveStep(0);
    setImportType(null);
    setParsedCSV(null);
    setFieldMappings([]);
    setRelationshipMappings([]);
    setImportedRecords([]);
    setError('');
    setImportComplete(false);
    setImportedCount(0);
    setRelationshipStats({});
  };

  // Tour-driven walkthrough (step 18). Pretends a user picked Courses
  // from the type tiles, dropped a CSV with five rows, advanced
  // through field mapping, and landed on the preview screen — but
  // STOPS short of the final import. fnRef keeps the trigger handler
  // pointed at the latest initializeFieldMappings / handleMappingComplete
  // closures so the state mutations they perform read the current
  // importType / parsedCSV (the empty-deps useEffect would otherwise
  // capture initial-render closures).
  const fnRef = useRef({ initializeFieldMappings, handleMappingComplete });
  useEffect(() => { fnRef.current = { initializeFieldMappings, handleMappingComplete }; });
  useEffect(() => {
    let ownerStepId: string | null = null;
    const timers: number[] = [];
    const cancelTimers = () => {
      timers.forEach((t) => window.clearTimeout(t));
      timers.length = 0;
    };
    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const stepId: string | undefined = detail?.stepId;
      if (detail?.trigger === 'bulk-import-courses-walkthrough') {
        ownerStepId = stepId ?? null;
        cancelTimers();
        // Beat 1: pick the Courses tile.
        setImportType('courses');
        setActiveStep(0);
        // Beat 2 (+1800ms): pretend a CSV was uploaded — parse a
        // fabricated 5-row roster, init mappings, advance to step 1
        // (Map fields).
        timers.push(window.setTimeout(() => {
          const csv = [
            'Name,Description,Instructor,Capacity,Schedule,Location,Start Date,End Date',
            'Algebra II,Advanced algebra concepts,Ms. Patterson,28,MWF 9:00-10:00,Room 204,2026-08-25,2027-05-30',
            'AP Biology,College-level biology,Dr. Reeves,24,TTh 10:30-12:00,Lab 3,2026-08-25,2027-05-30',
            'World History,Survey of world history,Mr. Chen,30,MWF 11:00-12:00,Room 109,2026-08-25,2027-05-30',
            'Spanish III,Intermediate Spanish,Sra. Lopez,26,MWF 1:00-2:00,Room 215,2026-08-25,2027-05-30',
            'Studio Art,Mixed media studio practice,Ms. Reilly,20,TTh 2:00-3:30,Art Studio,2026-08-25,2027-05-30',
          ].join('\n');
          try {
            const parsed = parseCSV(csv);
            setParsedCSV(parsed);
            fnRef.current.initializeFieldMappings(parsed);
            setActiveStep(1);
          } catch { /* noop */ }
        }, 1800));
        // Beat 3 (+5500ms): commit the mappings and advance to the
        // Preview step. Stops here — no handleConfirmImport — so the
        // booth audience sees the validated rows but nothing actually
        // imports.
        timers.push(window.setTimeout(() => {
          fnRef.current.handleMappingComplete();
        }, 5500));
      } else if (ownerStepId !== null && stepId !== ownerStepId) {
        ownerStepId = null;
        cancelTimers();
        handleReset();
      }
    };
    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => {
      window.removeEventListener('cc-tour-trigger', onTrigger);
      cancelTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBack = () => {
    if (activeStep === 0) {
      setImportType(null);
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleTypeSelection = (type: ImportType) => {
    setImportType(type);
    setActiveStep(0);
  };

  const getImportTypeInfo = (type: ImportType) => {
    switch (type) {
      case 'students':
        return {
          icon: <Users size={48} />,
          title: 'Students',
          description: 'Import student records including names, IDs, contact information, and grade levels'
        };
      case 'parents':
        return {
          icon: <UserCircle size={48} />,
          title: 'Parents',
          description: 'Import parent/guardian records with contact details and addresses'
        };
      case 'community':
        return {
          icon: <Building size={48} />,
          title: 'Community Members',
          description: 'Import community member records including organization and role information'
        };
      case 'fees':
        return {
          icon: <DollarSign size={48} />,
          title: 'Fees',
          description: 'Import fee records with amounts, categories, due dates, and requirements'
        };
      case 'courses':
        return {
          icon: <GraduationCap size={48} />,
          title: 'Courses',
          description: 'Import course records including instructors, schedules, locations, and capacity'
        };
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Upload size={32} style={{ marginRight: 16 }} />
          <Typography variant="h4">Bulk Import</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {!importType ? (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Select the type of data you want to import:
            </Typography>
            <Grid container spacing={3}>
              {(['students', 'parents', 'community', 'fees', 'courses'] as ImportType[]).map((type) => {
                const info = getImportTypeInfo(type);
                return (
                  <Grid item xs={12} sm={6} md={4} key={type}>
                    <Card
                      sx={{
                        height: '100%',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleTypeSelection(type)}
                        sx={{ height: '100%', p: 2 }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <Box sx={{ mb: 2, color: 'primary.main' }}>
                              {info.icon}
                            </Box>
                            <Typography variant="h6" gutterBottom>
                              {info.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {info.description}
                            </Typography>
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ) : (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }} data-tour="bulk-import-steps">
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box>
              {activeStep === 0 && (
                <UploadStep
                  importType={importType}
                  onFileUpload={handleFileUpload}
                />
              )}

              {activeStep === 1 && parsedCSV && (
                <MappingStep
                  parsedCSV={parsedCSV}
                  fieldMappings={fieldMappings}
                  onMappingsChange={setFieldMappings}
                  relationshipMappings={relationshipMappings}
                  onRelationshipMappingsChange={setRelationshipMappings}
                  importType={importType}
                />
              )}

              {activeStep === 2 && (
                <PreviewStep
                  importedRecords={importedRecords}
                  fieldMappings={fieldMappings}
                  relationshipMappings={relationshipMappings}
                  importType={importType}
                  onRecordsChange={setImportedRecords}
                />
              )}

              {activeStep === 3 && (
                <ConfirmationStep
                  importedCount={importedCount}
                  importType={importType}
                  relationshipStats={relationshipStats}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                startIcon={<ArrowLeft />}
                onClick={handleBack}
                disabled={importComplete}
              >
                Back
              </Button>

              <Box sx={{ display: 'flex', gap: 2 }}>
                {activeStep === 1 && (
                  <Button
                    variant="contained"
                    endIcon={<ArrowRight />}
                    onClick={handleMappingComplete}
                    disabled={!fieldMappings.some(m => m.csvColumn !== null)}
                  >
                    Continue to Preview
                  </Button>
                )}

                {activeStep === 2 && (
                  <Button
                    variant="contained"
                    endIcon={<Check />}
                    onClick={handleConfirmImport}
                    disabled={!importedRecords.some(r => r.isValid)}
                  >
                    Confirm Import
                  </Button>
                )}

                {activeStep === 3 && (
                  <Button
                    variant="contained"
                    onClick={handleReset}
                  >
                    Start New Import
                  </Button>
                )}
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};
